import { useState } from "react";
import {
  BookOpen,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  CreditCard,
  FileText,
  HelpCircle,
  LifeBuoy,
  MessageSquare,
  Package,
  Search,
  ShieldCheck,
  Sparkles,
  Tag,
  Truck,
} from "lucide-react";
import { brandEmail } from "@/lib/brand";

const FAQs = [
  {
    category: "Orders & Shipping",
    icon: Truck,
    question: "How do I process and advance an incoming order status?",
    answer:
      "Go to the 'Orders' tab in the left sidebar. New pending orders automatically float to the top of the list. Click the dark 'ADVANCE' button next to an order to progress its status from Pending ➔ Packed ➔ Shipped ➔ Delivered.",
  },
  {
    category: "WhatsApp Confirmations",
    icon: MessageSquare,
    question: "How do I send direct WhatsApp confirmations to customers?",
    answer:
      "For any order with 'Pending' status, click the 3-dots (...) dropdown menu on the far right of the order row and select 'WhatsApp customer'. An editable message pre-filled with order ID, customer name, and order total will pop up before opening WhatsApp Web.",
  },
  {
    category: "Invoices & Printing",
    icon: FileText,
    question: "Where are gift notes printed on customer invoices?",
    answer:
      "When you click 'View Invoice' or 'Print Invoice' on any order card or details modal, the personalized gift note and envelope text are automatically formatted in a dedicated pink border box right above the item summary.",
  },
  {
    category: "Coupons & Promotions",
    icon: Tag,
    question: "How do I create a storewide or item-specific discount coupon?",
    answer:
      "Navigate to 'Coupons' in the sidebar and click 'Create Coupon'. Enter your code, discount percentage or fixed amount, and minimum order. Use the 'Select All Products' button to apply it storewide or check individual products to scope the discount to specific gifts.",
  },
  {
    category: "Catalog & Media",
    icon: Package,
    question: "Can I add product videos and high-res images?",
    answer:
      "Yes! When editing or adding a product in the 'Products' section, use the Media Uploader to attach multiple high-resolution photos and product demo videos. Videos automatically display an interactive badge on storefront product pages.",
  },
  {
    category: "Customer LTV & History",
    icon: ShieldCheck,
    question: "How do I view a customer's lifetime value and order history?",
    answer:
      "Open the 'Customers' page and click on any customer card. A detailed modal opens showing their total spend, lifetime orders, individual order dates, gift notes, and a direct 'View Order Details ➔' link to inspect any order instantly.",
  },
];

export function AdminHelp() {
  const [search, setSearch] = useState("");
  const [openIndex, setOpenIndex] = useState(0);

  const filteredFaqs = FAQs.filter(
    (faq) =>
      faq.question.toLowerCase().includes(search.toLowerCase()) ||
      faq.answer.toLowerCase().includes(search.toLowerCase()) ||
      faq.category.toLowerCase().includes(search.toLowerCase()),
  );

  return (
    <div className="space-y-6 max-w-5xl">
      <div>
        <p className="text-[10px] font-bold uppercase tracking-[0.25em] text-[#64748b]">
          Documentation & Manual
        </p>
        <h1 className="mt-1 font-display text-3xl font-bold leading-tight text-[#0f172a]">
          Help & Operational Guide
        </h1>
        <p className="mt-1 text-sm text-[#64748b]">
          Step-by-step guides, system diagnostics, and quick reference manual for managing Alaira House storefront operations.
        </p>
      </div>

      {/* System Status Banner */}
      <div className="grid gap-3 sm:grid-cols-3">
        <div className="rounded-2xl border border-[#e2e8f0]/60 bg-white p-4 shadow-sm">
          <div className="flex items-center gap-2 text-xs font-bold text-[#64748b] uppercase tracking-wider">
            <span className="h-2 w-2 rounded-full bg-[#10b981] animate-pulse" />
            MongoDB Database
          </div>
          <p className="mt-2 text-lg font-bold text-[#0f172a]">Connected 🟢</p>
          <p className="text-[11px] text-[#64748b]">Collections synced in real-time</p>
        </div>

        <div className="rounded-2xl border border-[#e2e8f0]/60 bg-white p-4 shadow-sm">
          <div className="flex items-center gap-2 text-xs font-bold text-[#64748b] uppercase tracking-wider">
            <span className="h-2 w-2 rounded-full bg-[#10b981] animate-pulse" />
            Razorpay Payments
          </div>
          <p className="mt-2 text-lg font-bold text-[#0f172a]">Active (Live) 🟢</p>
          <p className="text-[11px] text-[#64748b]">Instant checkout enabled</p>
        </div>

        <div className="rounded-2xl border border-[#e2e8f0]/60 bg-white p-4 shadow-sm">
          <div className="flex items-center gap-2 text-xs font-bold text-[#64748b] uppercase tracking-wider">
            <span className="h-2 w-2 rounded-full bg-[#10b981] animate-pulse" />
            Node / Express Server
          </div>
          <p className="mt-2 text-lg font-bold text-[#0f172a]">Port 5001 Online 🟢</p>
          <p className="text-[11px] text-[#64748b]">Dev SPA port: 3000</p>
        </div>
      </div>

      {/* FAQ Search */}
      <div className="rounded-2xl border border-[#e2e8f0]/60 bg-white p-6 shadow-sm space-y-5">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="flex items-center gap-2 font-display text-xl font-bold text-[#0f172a]">
            <BookOpen className="text-[#0f172a]" size={20} />
            Admin Operations Manual
          </div>
          <div className="relative w-full sm:w-72">
            <input
              type="text"
              placeholder="Search help topics…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="h-10 w-full rounded-xl border border-[#e2e8f0] bg-[#f8fafc]/50 pl-9 pr-3.5 text-xs font-semibold text-[#0f172a] outline-none focus:border-[#0f172a]"
            />
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#64748b]" />
          </div>
        </div>

        <div className="divide-y divide-[#f1f5f9]">
          {filteredFaqs.length === 0 ? (
            <div className="py-8 text-center text-xs text-[#64748b]">
              No help topics matching "{search}".
            </div>
          ) : (
            filteredFaqs.map((faq, index) => {
              const isOpen = openIndex === index;
              const Icon = faq.icon;
              return (
                <div key={index} className="py-3.5">
                  <button
                    type="button"
                    onClick={() => setOpenIndex(isOpen ? -1 : index)}
                    className="flex w-full items-center justify-between text-left text-sm font-bold text-[#0f172a] hover:text-[#5d0710] transition"
                  >
                    <span className="flex items-center gap-3">
                      <span className="grid h-7 w-7 place-items-center rounded-lg bg-[#f8fafc] text-[#0f172a] border border-[#e2e8f0]">
                        <Icon size={14} />
                      </span>
                      {faq.question}
                    </span>
                    {isOpen ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                  </button>

                  {isOpen && (
                    <div className="mt-2.5 pl-10 text-xs leading-relaxed text-[#64748b] bg-[#f8fafc]/50 p-3.5 rounded-xl border border-[#e2e8f0]/40">
                      <span className="mb-1 inline-block text-[9px] font-bold uppercase tracking-widest text-[#0f172a] bg-[#0f172a]/10 px-2 py-0.5 rounded-md">
                        {faq.category}
                      </span>
                      <p className="mt-1">{faq.answer}</p>
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* Support Card */}
      <div className="rounded-2xl border border-[#e2e8f0]/60 bg-gradient-to-br from-[#0f172a] to-[#1e293b] p-6 text-white shadow-lg">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <span className="inline-flex items-center gap-1.5 rounded-full bg-white/10 px-3 py-1 text-[10px] font-bold uppercase tracking-widest text-white mb-2">
              <LifeBuoy size={13} /> Need Technical Support?
            </span>
            <h3 className="font-display text-xl font-bold">Have a custom feature request or issue?</h3>
            <p className="text-xs text-white/70 mt-1 max-w-lg">
              Our engineering team is available for real-time storefront support, custom integration updates, and database maintenance.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2 shrink-0">
            <a
              href="https://wa.me/917841928485?text=Hello!%20I%20need%20admin%20technical%20support%20for%20Alaira%20House."
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 rounded-full bg-[#25D366] px-5 py-3 text-xs font-bold uppercase tracking-widest text-white hover:bg-[#1eb956] transition shadow-md"
            >
              💬 WhatsApp (+91 78419 28485)
            </a>
            <a
              href={`mailto:${brandEmail}?subject=Alaira%20House%20Admin%20Support%20Request`}
              className="inline-flex items-center justify-center gap-2 rounded-full bg-white px-5 py-3 text-xs font-bold uppercase tracking-widest text-[#0f172a] hover:bg-[#f8fafc] transition shadow-md"
            >
              Email ✉️
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}
